"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TodayStats } from "@/services/dashboardService";
import { CheckCircle, UserCheck, Clock, XCircle, CalendarX, AlertCircle } from "lucide-react";

interface StatusBreakdownCardProps {
  stats: TodayStats | null;
  loading: boolean;
}

export function StatusBreakdownCard({ stats, loading }: StatusBreakdownCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">No data available</div>
        </CardContent>
      </Card>
    );
  }

  const statusItems = [
    {
      label: "Completed",
      value: stats.statusCounts.completed,
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      label: "Assigned",
      value: stats.statusCounts.assigned,
      icon: UserCheck,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      label: "In Progress",
      value: stats.statusCounts.inProgress,
      icon: Clock,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
    },
    {
      label: "Not Completed",
      value: stats.statusCounts.notCompleted,
      icon: AlertCircle,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      label: "Postponed",
      value: stats.statusCounts.postponed,
      icon: CalendarX,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      label: "Cancelled",
      value: stats.statusCounts.cancelled,
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Status Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {statusItems.map((item) => (
            <div
              key={item.label}
              className={`flex items-center gap-3 p-3 rounded-lg ${item.bgColor}`}
            >
              <item.icon className={`h-5 w-5 ${item.color}`} />
              <div>
                <div className="text-lg font-bold">{item.value}</div>
                <div className="text-xs text-muted-foreground">{item.label}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
